import type NativeArray from '@ember/array/-private/native-array';
import type ArrayProxy from '@ember/array/proxy';
import { get } from '@ember/object';
import { service } from '@ember/service';
import type { AsyncBelongsTo, AsyncHasMany, SyncHasMany } from '@ember-data/model';
import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import type EmployeeModel from 'garaje/models/employee';
import type FlowModel from 'garaje/models/flow';
import type LocationModel from 'garaje/models/location';
import type RecurringGroupInvite from 'garaje/models/recurring-group-invite';
import type UserModel from 'garaje/models/user';
import type UserDocumentTemplateModel from 'garaje/models/user-document-template';
import type { Identifier } from 'garaje/models/user-document-template';
import type VisitorDocumentModel from 'garaje/models/visitor-document';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type PlainObject from 'garaje/utils/plain-object';
import type { RecordArray } from 'garaje/utils/type-utils';
import urlBuilder from 'garaje/utils/url-builder';
import { not } from 'macro-decorators';

export default class RecurringInvite extends Model {
  @service declare ajaxFetch: AjaxFetchService;

  @belongsTo('user', { async: true, inverse: null }) declare creator: AsyncBelongsTo<UserModel>;
  @belongsTo('employee', { async: true, inverse: null }) declare employee?:
    | AsyncBelongsTo<EmployeeModel>
    | EmployeeModel;
  @belongsTo('flow', { async: true, inverse: null }) declare flow: AsyncBelongsTo<FlowModel>;
  @belongsTo('location', { async: true, inverse: null }) declare location?:
    | AsyncBelongsTo<LocationModel>
    | LocationModel;
  @belongsTo('recurring-invite', { async: true, inverse: null }) declare parentRecurringInvite:
    | AsyncBelongsTo<RecurringInvite>
    | RecurringInvite;
  @belongsTo('recurring-group-invite', { async: true, inverse: null })
  declare recurringGroupInvite?: AsyncBelongsTo<RecurringGroupInvite>;

  @hasMany('visitor-document', { async: false, inverse: 'recurringInvites' }) declare visitorDocuments:
    | SyncHasMany<VisitorDocumentModel>
    | RecordArray<VisitorDocumentModel>
    | NativeArray<VisitorDocumentModel>;
  @hasMany('location', { async: true, inverse: null }) declare childLocations?:
    | AsyncHasMany<LocationModel>
    | LocationModel[]
    | ArrayProxy<LocationModel>
    | null;

  @attr('string') declare email: string;
  @attr('string') declare fullName: string;
  @attr('number', { defaultValue: 0 }) declare additionalGuests: number;
  @attr('string') declare privateNotes: string;
  @attr('string') declare propertyNotes: string;
  @attr('locality', { defaultValue: () => ({ placeId: '' }) }) declare locality: { placeId: string };
  @attr('string') declare recurringRule: string;
  @attr('boolean', { defaultValue: false }) declare skipGuestNotification: boolean;
  @attr('date') declare startTime: Date;
  @attr('array', { defaultValue: () => [] }) declare locationNames: string[];
  @attr('userData', { defaultValue: () => [] }) declare userData: unknown;

  // SQ-4574: Invert boolean in model to set same value in component
  @not('skipGuestNotification') sendGuestEmail!: boolean;

  notifyInvite(data: PlainObject): Promise<unknown> {
    return this.ajaxFetch.request(urlBuilder.v3.recurringInvites.sendNotification(this.id), {
      contentType: 'application/json',
      data,
      type: 'POST',
    });
  }

  visitorDocumentForTemplate(userDocumentTemplate: UserDocumentTemplateModel): VisitorDocumentModel | undefined {
    // eslint-disable-next-line ember/no-get
    return this.visitorDocumentForIdentifier(get(userDocumentTemplate, 'identifier'));
  }

  visitorDocumentForIdentifier(identifier: Identifier): VisitorDocumentModel | undefined {
    return this.visitorDocuments.findBy('identifier', identifier);
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    'recurring-invite': RecurringInvite;
  }
}
